import type React from 'react';
import type { CalculationMode, CalculationResult } from './types';
import { formatCurrency, formatShortCurrency, getModeTitle } from './calculations';

export interface LoanParametersProps {
  calcMode: CalculationMode;
  onModeChange: (mode: CalculationMode) => void;
  amountStr: string;
  setAmountStr: (val: string) => void;
  rateStr: string;
  setRateStr: (val: string) => void;
  yearsStr: string;
  setYearsStr: (val: string) => void;
  monthsStr: string;
  setMonthsStr: (val: string) => void;
  emiStr: string;
  setEmiStr: (val: string) => void;
  amount: number;
  rate: number;
  years: number;
  months: number;
  emiInputVal: number;
  totalMonthsInput: number;
  calcResult: CalculationResult;
  isCurrentModeCalculated: boolean;
  onCalculate: () => void;
  onClear: () => void;
}

export const LoanParameters: React.FC<LoanParametersProps> = ({
  calcMode,
  onModeChange,
  amountStr,
  setAmountStr,
  rateStr,
  setRateStr,
  yearsStr,
  setYearsStr,
  monthsStr,
  setMonthsStr,
  emiStr,
  setEmiStr,
  amount,
  rate,
  years,
  months,
  emiInputVal,
  totalMonthsInput,
  calcResult,
  isCurrentModeCalculated,
  onCalculate,
  onClear,
}) => {
  return (
    <div className="lg:col-span-7 bg-white rounded-3xl p-6 border border-gray-200 shadow-sm space-y-5">
      {/* Parameters Header */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-3">
        <div>
          <h2 className="text-lg font-extrabold text-gray-900">Loan Parameters</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            The selected row is the calculated output. The other three rows are editable inputs.
          </p>
        </div>
        <span className="text-xs font-semibold text-brand-700 bg-brand-50 border border-brand-200 px-2.5 py-1 rounded-lg shrink-0">
          Reducing Balance
        </span>
      </div>

      {/* Validation Notice Banner */}
      {calcResult.error && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-2xl flex items-start gap-3 text-amber-900 text-xs">
          <svg
            className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
          <div>
            <p className="font-extrabold text-amber-900 text-sm">Calculation Notice</p>
            <p className="mt-0.5 text-amber-800 leading-relaxed">{calcResult.error}</p>
          </div>
        </div>
      )}

      {/* 1. Principal Loan Amount Row */}
      <div
        className={`p-4 rounded-2xl transition-all ${
          calcMode === 'principal'
            ? 'bg-brand-50/40 border-2 border-brand-500 ring-2 ring-brand-500/20 shadow-xs'
            : 'bg-white border border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <label
            onClick={() => onModeChange('principal')}
            className="flex items-center gap-2.5 cursor-pointer select-none"
          >
            <input
              type="radio"
              name="fieldRadio"
              checked={calcMode === 'principal'}
              onChange={() => onModeChange('principal')}
              className="w-4 h-4 text-brand-600 focus:ring-brand-500 border-gray-300 accent-brand-600 cursor-pointer"
            />
            <span className="text-xs font-extrabold text-gray-800 uppercase tracking-wide">
              Loan Amount (₹)
            </span>
          </label>

          {calcMode === 'principal' ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-black text-brand-800 bg-brand-100 border border-brand-300 px-2.5 py-0.5 rounded-full shadow-2xs">
              <svg
                className="w-3 h-3 text-brand-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Calculated Output
            </span>
          ) : (
            <span className="text-[11px] text-gray-400 font-medium">Editable Input</span>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 mt-2">
          <div className="relative flex-1 sm:flex-initial">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
              ₹
            </span>
            <input
              type={calcMode === 'principal' ? 'text' : 'number'}
              min={0}
              max={100000000}
              step={1000}
              placeholder="0"
              readOnly={calcMode === 'principal'}
              value={
                calcMode === 'principal'
                  ? isCurrentModeCalculated && !calcResult.error
                    ? formatCurrency(calcResult.resolvedPrincipal, 2).replace('₹', '').trim()
                    : 'Click Calculate'
                  : amountStr
              }
              onFocus={(e) => {
                if (calcMode !== 'principal') e.target.select();
              }}
              onChange={(e) => setAmountStr(e.target.value)}
              className={`w-full sm:w-52 pl-7 pr-3 py-1.5 rounded-xl border text-right text-base font-extrabold transition-all ${
                calcMode === 'principal'
                  ? 'border-brand-400 bg-brand-50/80 text-brand-900 cursor-default shadow-2xs'
                  : 'border-gray-300 font-extrabold text-brand-800 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/20'
              }`}
            />
          </div>

          {calcMode === 'principal' && (
            <span className="text-xs text-brand-700 font-semibold hidden sm:inline">
              Calculated from Rate, Tenure & EMI
            </span>
          )}
        </div>

        <div className="mt-3">
          <input
            type="range"
            min={0}
            max={50000000}
            step={10000}
            disabled={calcMode === 'principal'}
            value={
              calcMode === 'principal'
                ? isCurrentModeCalculated
                  ? Math.min(50000000, calcResult.resolvedPrincipal)
                  : 0
                : amount
            }
            onChange={(e) => setAmountStr(String(e.target.value))}
            className={`w-full h-2 rounded-lg appearance-none ${
              calcMode === 'principal'
                ? 'bg-brand-200/60 cursor-not-allowed opacity-60 accent-brand-600'
                : 'bg-gray-200 cursor-pointer accent-brand-700'
            }`}
          />
          <div className="flex justify-between text-[11px] text-gray-400 font-medium mt-1">
            <span>₹ 0</span>
            <span className="font-bold text-gray-600">
              {formatShortCurrency(
                calcMode === 'principal'
                  ? isCurrentModeCalculated
                    ? calcResult.resolvedPrincipal
                    : 0
                  : amount,
              )}
            </span>
            <span>₹ 5 Cr</span>
          </div>
        </div>
      </div>

      {/* 2. Annual Interest Rate Row */}
      <div
        className={`p-4 rounded-2xl transition-all ${
          calcMode === 'rate'
            ? 'bg-brand-50/40 border-2 border-brand-500 ring-2 ring-brand-500/20 shadow-xs'
            : 'bg-white border border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <label
            onClick={() => onModeChange('rate')}
            className="flex items-center gap-2.5 cursor-pointer select-none"
          >
            <input
              type="radio"
              name="fieldRadio"
              checked={calcMode === 'rate'}
              onChange={() => onModeChange('rate')}
              className="w-4 h-4 text-brand-600 focus:ring-brand-500 border-gray-300 accent-brand-600 cursor-pointer"
            />
            <span className="text-xs font-extrabold text-gray-800 uppercase tracking-wide">
              Annual Interest Rate (% p.a.)
            </span>
          </label>

          {calcMode === 'rate' ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-black text-brand-800 bg-brand-100 border border-brand-300 px-2.5 py-0.5 rounded-full shadow-2xs">
              <svg
                className="w-3 h-3 text-brand-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Calculated Output
            </span>
          ) : (
            <span className="text-[11px] text-gray-400 font-medium">Editable Input</span>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 mt-2">
          <div className="relative flex-1 sm:flex-initial">
            <input
              type={calcMode === 'rate' ? 'text' : 'number'}
              min={0}
              max={50}
              step={0.1}
              placeholder="0"
              readOnly={calcMode === 'rate'}
              value={
                calcMode === 'rate'
                  ? isCurrentModeCalculated && !calcResult.error
                    ? calcResult.resolvedRate.toFixed(2)
                    : 'Click Calculate'
                  : rateStr
              }
              onFocus={(e) => {
                if (calcMode !== 'rate') e.target.select();
              }}
              onChange={(e) => setRateStr(e.target.value)}
              className={`w-full sm:w-36 pr-7 pl-3 py-1.5 rounded-xl border text-right text-base font-extrabold transition-all ${
                calcMode === 'rate'
                  ? 'border-brand-400 bg-brand-50/80 text-brand-900 cursor-default shadow-2xs'
                  : 'border-gray-300 font-extrabold text-brand-800 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/20'
              }`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
              %
            </span>
          </div>

          {calcMode === 'rate' && (
            <span className="text-xs text-brand-700 font-semibold hidden sm:inline">
              Calculated from Amount, Tenure & EMI
            </span>
          )}
        </div>

        <div className="mt-3">
          <input
            type="range"
            min={0}
            max={30}
            step={0.25}
            disabled={calcMode === 'rate'}
            value={
              calcMode === 'rate'
                ? isCurrentModeCalculated
                  ? Math.min(30, calcResult.resolvedRate)
                  : 0
                : rate
            }
            onChange={(e) => setRateStr(String(e.target.value))}
            className={`w-full h-2 rounded-lg appearance-none ${
              calcMode === 'rate'
                ? 'bg-brand-200/60 cursor-not-allowed opacity-60 accent-brand-600'
                : 'bg-gray-200 cursor-pointer accent-brand-700'
            }`}
          />
          <div className="flex justify-between text-[11px] text-gray-400 font-medium mt-1">
            <span>0%</span>
            <span className="font-bold text-gray-600">
              {(calcMode === 'rate'
                ? isCurrentModeCalculated
                  ? calcResult.resolvedRate
                  : 0
                : rate
              ).toFixed(2)}
              % p.a.
            </span>
            <span>30%</span>
          </div>
        </div>
      </div>

      {/* 3. Loan Duration / Tenure Row */}
      <div
        className={`p-4 rounded-2xl transition-all space-y-4 ${
          calcMode === 'tenure'
            ? 'bg-brand-50/40 border-2 border-brand-500 ring-2 ring-brand-500/20 shadow-xs'
            : 'bg-white border border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center justify-between">
          <label
            onClick={() => onModeChange('tenure')}
            className="flex items-center gap-2.5 cursor-pointer select-none"
          >
            <input
              type="radio"
              name="fieldRadio"
              checked={calcMode === 'tenure'}
              onChange={() => onModeChange('tenure')}
              className="w-4 h-4 text-brand-600 focus:ring-brand-500 border-gray-300 accent-brand-600 cursor-pointer"
            />
            <span className="text-xs font-extrabold text-gray-800 uppercase tracking-wide">
              Loan Duration
            </span>
          </label>

          {calcMode === 'tenure' ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-black text-brand-800 bg-brand-100 border border-brand-300 px-2.5 py-0.5 rounded-full shadow-2xs">
              <svg
                className="w-3 h-3 text-brand-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Calculated Output
            </span>
          ) : (
            <span className="text-[11px] text-gray-400 font-medium">Editable Input</span>
          )}
        </div>

        {/* Years Slider & Input */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-500 font-semibold">Tenure in Years</span>
            <div className="flex items-center gap-1">
              <input
                type={calcMode === 'tenure' ? 'text' : 'number'}
                min={0}
                max={30}
                placeholder="0"
                readOnly={calcMode === 'tenure'}
                value={
                  calcMode === 'tenure'
                    ? isCurrentModeCalculated && !calcResult.error
                      ? String(calcResult.resolvedYears)
                      : '—'
                    : yearsStr
                }
                onFocus={(e) => {
                  if (calcMode !== 'tenure') e.target.select();
                }}
                onChange={(e) => setYearsStr(e.target.value)}
                className={`w-16 px-2 py-1 rounded-lg border text-right text-sm font-extrabold ${
                  calcMode === 'tenure'
                    ? 'border-brand-400 bg-brand-50/80 text-brand-900 cursor-default shadow-2xs'
                    : 'border-gray-300 text-brand-800 focus:border-brand-600 focus:outline-none'
                }`}
              />
              <span className="text-xs font-bold text-gray-600">Yrs</span>
            </div>
          </div>
          <input
            type="range"
            min={0}
            max={30}
            step={1}
            disabled={calcMode === 'tenure'}
            value={
              calcMode === 'tenure'
                ? isCurrentModeCalculated
                  ? Math.min(30, calcResult.resolvedYears)
                  : 0
                : years
            }
            onChange={(e) => setYearsStr(String(e.target.value))}
            className={`w-full h-2 rounded-lg appearance-none ${
              calcMode === 'tenure'
                ? 'bg-brand-200/60 cursor-not-allowed opacity-60 accent-brand-600'
                : 'bg-gray-200 cursor-pointer accent-brand-700'
            }`}
          />
          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
            <span>0 Yr</span>
            <span>15 Yrs</span>
            <span>30 Yrs</span>
          </div>
        </div>

        {/* Additional Months Slider & Input */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-500 font-semibold">Additional Months</span>
            <div className="flex items-center gap-1">
              <input
                type={calcMode === 'tenure' ? 'text' : 'number'}
                min={0}
                max={11}
                placeholder="0"
                readOnly={calcMode === 'tenure'}
                value={
                  calcMode === 'tenure'
                    ? isCurrentModeCalculated && !calcResult.error
                      ? String(calcResult.resolvedMonths)
                      : '—'
                    : monthsStr
                }
                onFocus={(e) => {
                  if (calcMode !== 'tenure') e.target.select();
                }}
                onChange={(e) => setMonthsStr(e.target.value)}
                className={`w-16 px-2 py-1 rounded-lg border text-right text-sm font-extrabold ${
                  calcMode === 'tenure'
                    ? 'border-brand-400 bg-brand-50/80 text-brand-900 cursor-default shadow-2xs'
                    : 'border-gray-300 text-brand-800 focus:border-brand-600 focus:outline-none'
                }`}
              />
              <span className="text-xs font-bold text-gray-600">Mos</span>
            </div>
          </div>
          <input
            type="range"
            min={0}
            max={11}
            step={1}
            disabled={calcMode === 'tenure'}
            value={
              calcMode === 'tenure'
                ? isCurrentModeCalculated
                  ? calcResult.resolvedMonths
                  : 0
                : months
            }
            onChange={(e) => setMonthsStr(String(e.target.value))}
            className={`w-full h-2 rounded-lg appearance-none ${
              calcMode === 'tenure'
                ? 'bg-brand-200/60 cursor-not-allowed opacity-60 accent-brand-600'
                : 'bg-gray-200 cursor-pointer accent-brand-700'
            }`}
          />
          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
            <span>0 Mo</span>
            <span>6 Mos</span>
            <span>11 Mos</span>
          </div>
        </div>

        <div className="p-3 bg-brand-50/70 rounded-xl border border-brand-200 flex items-center justify-between text-xs font-semibold text-brand-900">
          <span>Total Duration:</span>
          <span className="font-extrabold text-brand-800">
            {calcMode === 'tenure'
              ? isCurrentModeCalculated && !calcResult.error && calcResult.resolvedTotalMonths > 0
                ? `${calcResult.resolvedTotalMonths} Months (${calcResult.resolvedYears} Yrs ${
                    calcResult.resolvedMonths > 0 ? `${calcResult.resolvedMonths} Mos` : ''
                  })`
                : 'Click Calculate to compute tenure'
              : `${totalMonthsInput} Months (${years} Yrs ${months > 0 ? `${months} Mos` : ''})`}
          </span>
        </div>
      </div>

      {/* 4. Monthly EMI Row */}
      <div
        className={`p-4 rounded-2xl transition-all ${
          calcMode === 'emi'
            ? 'bg-brand-50/40 border-2 border-brand-500 ring-2 ring-brand-500/20 shadow-xs'
            : 'bg-white border border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <label
            onClick={() => onModeChange('emi')}
            className="flex items-center gap-2.5 cursor-pointer select-none"
          >
            <input
              type="radio"
              name="fieldRadio"
              checked={calcMode === 'emi'}
              onChange={() => onModeChange('emi')}
              className="w-4 h-4 text-brand-600 focus:ring-brand-500 border-gray-300 accent-brand-600 cursor-pointer"
            />
            <span className="text-xs font-extrabold text-gray-800 uppercase tracking-wide">
              Monthly EMI (₹)
            </span>
          </label>

          {calcMode === 'emi' ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-black text-brand-800 bg-brand-100 border border-brand-300 px-2.5 py-0.5 rounded-full shadow-2xs">
              <svg
                className="w-3 h-3 text-brand-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Calculated Output
            </span>
          ) : (
            <span className="text-[11px] text-gray-400 font-medium">Editable Input</span>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 mt-2">
          <div className="relative flex-1 sm:flex-initial">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
              ₹
            </span>
            <input
              type={calcMode === 'emi' ? 'text' : 'number'}
              min={0}
              max={50000000}
              step={10}
              placeholder="0"
              readOnly={calcMode === 'emi'}
              value={
                calcMode === 'emi'
                  ? isCurrentModeCalculated && !calcResult.error
                    ? formatCurrency(calcResult.resolvedEmi, 2).replace('₹', '').trim()
                    : 'Click Calculate'
                  : emiStr
              }
              onFocus={(e) => {
                if (calcMode !== 'emi') e.target.select();
              }}
              onChange={(e) => setEmiStr(e.target.value)}
              className={`w-full sm:w-52 pl-7 pr-3 py-1.5 rounded-xl border text-right text-base font-extrabold transition-all ${
                calcMode === 'emi'
                  ? 'border-brand-400 bg-brand-50/80 text-brand-900 cursor-default shadow-2xs'
                  : 'border-gray-300 font-extrabold text-brand-800 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/20'
              }`}
            />
          </div>

          {calcMode === 'emi' && (
            <span className="text-xs text-brand-700 font-semibold hidden sm:inline">
              Calculated from Amount, Rate & Tenure
            </span>
          )}
        </div>

        <div className="mt-3">
          <input
            type="range"
            min={0}
            max={500000}
            step={500}
            disabled={calcMode === 'emi'}
            value={
              calcMode === 'emi'
                ? isCurrentModeCalculated
                  ? Math.min(500000, Math.round(calcResult.resolvedEmi))
                  : 0
                : emiInputVal
            }
            onChange={(e) => setEmiStr(String(e.target.value))}
            className={`w-full h-2 rounded-lg appearance-none ${
              calcMode === 'emi'
                ? 'bg-brand-200/60 cursor-not-allowed opacity-60 accent-brand-600'
                : 'bg-gray-200 cursor-pointer accent-brand-700'
            }`}
          />
          <div className="flex justify-between text-[11px] text-gray-400 font-medium mt-1">
            <span>₹ 0</span>
            <span className="font-bold text-gray-600">
              {formatShortCurrency(
                calcMode === 'emi'
                  ? isCurrentModeCalculated
                    ? calcResult.resolvedEmi
                    : 0
                  : emiInputVal,
              )}
            </span>
            <span>₹ 5 Lakh</span>
          </div>
        </div>
      </div>

      {/* Action Buttons: Calculate + Clear / Reset */}
      <div className="pt-3 flex flex-wrap items-center justify-between gap-3 border-t border-gray-150">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            type="button"
            onClick={onCalculate}
            className="flex-1 sm:flex-initial inline-flex items-center justify-center px-6 py-2.5 bg-brand-700 hover:bg-brand-800 active:scale-95 text-white font-extrabold text-sm rounded-xl shadow-xs transition-all cursor-pointer"
          >
            <span>Calculate {getModeTitle(calcMode)}</span>
          </button>

          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-700 hover:text-gray-900 font-bold text-sm rounded-xl border border-gray-300 transition-all cursor-pointer"
            title="Clear all calculator values"
          >
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            <span>Clear</span>
          </button>
        </div>

        <div className="text-[11px] text-gray-500 font-medium text-center sm:text-right">
          {isCurrentModeCalculated && !calcResult.error ? (
            <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Calculated & Synchronized
            </span>
          ) : (
            'Click Calculate to solve the selected unknown'
          )}
        </div>
      </div>
    </div>
  );
};
